import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { restaurantCategoryQueries } from '../api/restaurant-category-queries';
import { categoryDisplayName } from '../lib/category-ids';

/** Single restaurant category detail by id. */
export function useRestaurantCategory(id: string | undefined, enabled = true) {
  return useQuery(restaurantCategoryQueries.detail(id ?? '', enabled && Boolean(id)));
}

export interface RestaurantCategoryNames {
  /** Display name per id, in input order (short id while unresolved). */
  names: string[];
  /** True while any rubric detail is still loading. */
  isLoading: boolean;
}

/**
 * Rubric display names for a branch's `restaurant_category_ids`, resolved
 * through the cached category-detail query (one request per distinct id).
 */
export function useRestaurantCategoryNames(
  ids: readonly string[],
): RestaurantCategoryNames {
  const uniqueIds = useMemo(() => [...new Set(ids)].sort(), [ids]);
  const results = useQueries({
    queries: uniqueIds.map((id) => restaurantCategoryQueries.detail(id, true)),
  });
  const namesById = useMemo(() => {
    const map = new Map<string, string>();
    uniqueIds.forEach((id, index) => {
      map.set(id, categoryDisplayName(results[index]?.data?.name, id));
    });
    return map;
  }, [uniqueIds, results]);
  return useMemo(
    () => ({
      names: ids.map((id) => namesById.get(id) ?? categoryDisplayName(undefined, id)),
      isLoading: results.some((result) => result.isLoading),
    }),
    [ids, namesById, results],
  );
}
